import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaCheck,
  FaChevronDown,
  FaCommentDots,
  FaComments,
  FaInbox,
  FaSearch,
  FaUser,
  FaWhatsapp,
} from "react-icons/fa";
import { FaCheckDouble } from "react-icons/fa6";
import {
  fetchCustomerThread,
  fetchInactiveContacts,
} from "../../api/chat.api";
import { useProjectPhone } from "../../hooks/useProjectPhone";
import WhatsAppConnectPrompt from "./WhatsAppConnectPrompt";
import {
  avatarLetter,
  deliveryTick,
  formatBubbleTime,
  formatDayLabel,
  formatListTime,
  formatMobile,
  formatTime,
  inboxAvatarTone,
  lastMessagePreview,
  messageDayKey,
  messagePreview,
} from "../../utils/chatHelpers";

const DETAIL_SECTIONS = [
  "Attribute",
  "Accounts Responsibility",
  "Campaign",
  "Notes",
  "Payment",
];

function normalizeInactiveContactsResponse(res) {
  if (Array.isArray(res)) {
    return { customers: res, total: res.length };
  }
  return {
    customers: Array.isArray(res?.customers) ? res.customers : [],
    total: res?.total ?? (Array.isArray(res?.customers) ? res.customers.length : 0),
  };
}

function renderDetailContent(label, contact) {
  if (!contact) return null;

  if (label === "Attribute") {
    const attrs = contact.attributes;
    if (!Array.isArray(attrs) || !attrs.length) return null;
    return (
      <ul className="history-details-list">
        {attrs.map((attr, idx) => (
          <li key={attr.name || attr.key || idx}>
            <strong>{attr.name || attr.key || "Attribute"}:</strong>{" "}
            {attr.value ?? String(attr)}
          </li>
        ))}
      </ul>
    );
  }

  if (label === "Notes") {
    const text = contact.note?.text;
    if (!text) return null;
    return <p>{text}</p>;
  }

  if (label === "Campaign") {
    const campaigns = contact.campaign;
    if (!Array.isArray(campaigns) || !campaigns.length) return null;
    return (
      <ul className="history-details-list">
        {campaigns.map((id) => (
          <li key={id}>{id}</li>
        ))}
      </ul>
    );
  }

  if (label === "Accounts Responsibility") {
    const owner = contact.IntervenedBy || contact.ownerUserId;
    if (!owner) return null;
    return <p>{owner}</p>;
  }

  return null;
}

const History = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { phoneNoId, projectLoading, needsWhatsAppSetup } = useProjectPhone();
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState("");
  const [mobileThreadOpen, setMobileThreadOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchContacts = useCallback(async () => {
    if (projectLoading) return;
    if (!phoneNoId) {
      setContacts([]);
      setTotal(0);
      setLoadingContacts(false);
      setError("");
      return;
    }
    setLoadingContacts(true);
    setError("");
    try {
      const res = await fetchInactiveContacts(phoneNoId, {
        page,
        limit: perPage,
        search: debouncedSearch,
      });
      const { customers, total: totalCount } = normalizeInactiveContactsResponse(res);
      const list = [...customers].sort(
        (a, b) => (b.unReadMessageCount || 0) - (a.unReadMessageCount || 0),
      );
      setContacts(list);
      setTotal(totalCount);
    } catch (err) {
      setContacts([]);
      setError(err.message || "Failed to load history contacts");
    } finally {
      setLoadingContacts(false);
    }
  }, [phoneNoId, projectLoading, page, perPage, debouncedSearch]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const fetchMessages = useCallback(async (customerId) => {
    if (!customerId) return;
    setLoadingMessages(true);
    try {
      const res = await fetchCustomerThread(customerId, { limit: 20, skip: 0 });
      setMessages(Array.isArray(res?.messages) ? res.messages : []);
    } catch {
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  const selectContact = (contact) => {
    setSelected(contact);
    setMobileThreadOpen(true);
    fetchMessages(contact._id);
    setContacts((prev) =>
      prev.map((c) =>
        c._id === contact._id ? { ...c, unReadMessageCount: 0 } : c,
      ),
    );
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const openSendTemplate = () => {
    if (!projectId || !selected) return;
    navigate(`/businessprojects/${projectId}/sendtestmessage`, {
      state: { waid: selected.waid, name: selected.name },
    });
  };

  const accent = selected ? inboxAvatarTone(selected.name) : "green";

  const backToContactList = () => {
    setMobileThreadOpen(false);
    setSelected(null);
    setMessages([]);
  };

  if (projectLoading) {
    return (
      <div className="history-page livechat-page-loading">
        <div className="livechat-boot">
          <span className="livechat-boot-spinner" aria-hidden />
          <p>Loading history…</p>
        </div>
      </div>
    );
  }

  if (needsWhatsAppSetup) {
    return (
      <div className="history-page">
        <WhatsAppConnectPrompt
          projectId={projectId}
          title="Connect WhatsApp to view chat history"
          description="History shows past conversations for your linked WhatsApp Business number. Connect your number in project settings first."
        />
      </div>
    );
  }

  return (
    <div
      className={`history-page${
        mobileThreadOpen && selected ? " history-mobile-thread" : ""
      }`}
    >
      <aside className="livechat-contacts history-contacts">
        <div className="livechat-inbox-header">
          <div>
            <h1 className="livechat-inbox-title">History</h1>
            <p className="livechat-inbox-subtitle">Past conversations</p>
          </div>
          <span className="livechat-status is-idle">
            <span className="livechat-status-dot" />
            {total} chats
          </span>
        </div>

        <div className="livechat-search-row">
          <div className="livechat-search-wrap">
            <FaSearch className="livechat-search-icon" />
            <input
              type="search"
              className="livechat-search-input"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {error && <p className="biz-panel-error livechat-inline-error">{error}</p>}

        <div className="livechat-list" role="list">
          {loadingContacts ? (
            <div className="livechat-list-skel" aria-hidden>
              {[1, 2, 3, 4, 5, 6].map((row) => (
                <div key={row} className="livechat-skel-row">
                  <div className="livechat-skel-avatar-sm" />
                  <div className="livechat-skel-lines">
                    <span className="livechat-skel-line livechat-skel-line-md" />
                    <span className="livechat-skel-line livechat-skel-line-sm" />
                  </div>
                </div>
              ))}
            </div>
          ) : contacts.length === 0 ? (
            <div className="livechat-empty-list">
              <FaInbox aria-hidden />
              <strong>No conversations</strong>
              <span>No contacts in history yet.</span>
            </div>
          ) : (
            contacts.map((c) => {
              const unread = (c.unReadMessageCount || 0) > 0;
              return (
                <button
                  key={c._id}
                  type="button"
                  className={`livechat-item${
                    selected?._id === c._id ? " livechat-item-active" : ""
                  }${unread ? " has-unread" : ""}`}
                  onClick={() => selectContact(c)}
                >
                  <div
                    className={`livechat-avatar livechat-avatar-${inboxAvatarTone(c.name)}`}
                  >
                    {avatarLetter(c.name)}
                  </div>
                  <div className="livechat-item-main">
                    <div className="livechat-item-top">
                      <span className="livechat-name">{c.name || "Unknown"}</span>
                      <span className="livechat-time">
                        {formatListTime(c.lastActive)}
                      </span>
                    </div>
                    <div className="livechat-item-bottom">
                      <span className="livechat-preview">
                        {lastMessagePreview(c)}
                      </span>
                      {unread && (
                        <span className="livechat-unread">
                          {c.unReadMessageCount}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div className="history-pagination">
          <div className="history-pagination-left">
            <span>Show</span>
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
              className="history-perpage"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span>per page</span>
          </div>
          <div className="history-pagination-right">
            <button
              type="button"
              className="history-page-btn"
              disabled={page <= 1 || loadingContacts}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span className="history-page-indicator">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="history-page-btn"
              disabled={page >= totalPages || loadingContacts}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </aside>

      <section className="livechat-messages history-messages">
        {!selected ? (
          <div className="livechat-messages-empty">
            <span className="livechat-empty-icon">
              <FaComments />
            </span>
            <div className="livechat-empty-copy">
              <strong>Select a conversation</strong>
              <span>Choose a contact from history to view past messages.</span>
            </div>
          </div>
        ) : (
          <div className="livechat-thread">
            <div className="livechat-thread-header">
              <button
                type="button"
                className="livechat-back-btn"
                onClick={backToContactList}
                aria-label="Back to contacts"
              >
                <FaArrowLeft />
              </button>
              <div className={`livechat-avatar livechat-thread-avatar livechat-avatar-${accent}`}>
                {avatarLetter(selected.name)}
              </div>
              <div className="livechat-thread-identity">
                <strong>{selected.name || "Unknown contact"}</strong>
                <span className="livechat-thread-waid">
                  {formatMobile(selected.waid)}
                  {selected.lastActive
                    ? ` · Last active ${formatTime(selected.lastActive)}`
                    : ""}
                </span>
              </div>
              <span className="livechat-status-chip">History</span>
            </div>

            <div className="livechat-message-list">
              {loadingMessages ? (
                <p className="livechat-list-loading">Loading messages…</p>
              ) : messages.length === 0 ? (
                <div className="livechat-thread-empty">
                  <FaComments aria-hidden />
                  <strong>No messages yet</strong>
                  <span>Past messages for this contact will appear here.</span>
                </div>
              ) : (
                messages.map((msg, idx) => {
                  const ts = msg.createdAt || msg.timestamp;
                  const dayKey = messageDayKey(ts);
                  const prevDay =
                    idx > 0
                      ? messageDayKey(
                          messages[idx - 1].createdAt || messages[idx - 1].timestamp,
                        )
                      : "";
                  const showDay = Boolean(dayKey && dayKey !== prevDay);
                  const tick = msg.fromMe ? deliveryTick(msg.deliveryStatus) : null;
                  return (
                    <React.Fragment key={msg._id || msg.id || idx}>
                      {showDay && (
                        <div className="livechat-day-sep">
                          <span>{formatDayLabel(ts)}</span>
                        </div>
                      )}
                      <div
                        className={`livechat-message-bubble ${
                          msg.fromMe ? "outgoing" : "incoming"
                        }${
                          msg.fromMe && msg.deliveryStatus === "failed"
                            ? " is-failed"
                            : ""
                        }`}
                      >
                        <p>{messagePreview(msg)}</p>
                        <span className="livechat-message-meta">
                          <span className="livechat-message-time">
                            {formatBubbleTime(ts)}
                          </span>
                          {tick && (
                            <span
                              className={`livechat-ticks livechat-ticks-${String(
                                msg.deliveryStatus || "sent",
                              ).toLowerCase()}`}
                              title={tick.label}
                            >
                              {msg.deliveryStatus === "failed" ? (
                                "!"
                              ) : tick.icon === "double" ? (
                                <FaCheckDouble />
                              ) : (
                                <FaCheck />
                              )}
                            </span>
                          )}
                        </span>
                      </div>
                    </React.Fragment>
                  );
                })
              )}
            </div>

            <div className="history-thread-footer">
              <button
                type="button"
                className="history-send-template-btn"
                onClick={openSendTemplate}
              >
                <FaCommentDots aria-hidden />
                Send Template
              </button>
            </div>
          </div>
        )}
      </section>

      <aside className="livechat-details history-details">
        {selected ? (
          <>
            <div className="livechat-details-profile">
              <div className="livechat-details-avatar-wrap">
                <div
                  className={`livechat-details-avatar livechat-avatar-${accent}`}
                >
                  {avatarLetter(selected.name)}
                </div>
                <span className="livechat-details-wa-icon" aria-hidden>
                  <FaWhatsapp />
                </span>
              </div>
              <div className="livechat-details-profile-text">
                <strong>{selected.name || "Unknown contact"}</strong>
                <span>{formatMobile(selected.waid)}</span>
                <span className="livechat-status-chip">History</span>
              </div>
            </div>

            {DETAIL_SECTIONS.map((label) => {
              const content = renderDetailContent(label, selected);
              return (
                <details key={label} className="livechat-details-accordion">
                  <summary>
                    <span>{label}</span>
                    <FaChevronDown className="livechat-details-chevron" aria-hidden />
                  </summary>
                  <div className="livechat-details-accordion-body">
                    {content || (
                      <p>No {label.toLowerCase()} data available.</p>
                    )}
                  </div>
                </details>
              );
            })}
          </>
        ) : (
          <div className="livechat-details-empty">
            <span className="livechat-details-empty-icon">
              <FaUser aria-hidden />
            </span>
            <p className="livechat-details-title">Contact details</p>
            <p className="livechat-details-empty-copy">
              Select a conversation to see the customer profile, attributes, and notes.
            </p>
          </div>
        )}
      </aside>
    </div>
  );
};

export default History;
